import { Component, Inject, Input, OnInit } from '@angular/core';
import { ReportsService } from '../../../services/reports.service';
import { PaydateInfo, PayStubData, ReportPaydate } from '../../../models/reports';
import { BehaviorSubject, Observable } from 'rxjs';
import { CompaniesService } from '../../../services/companies.service';
import { CheckboxSizes } from '../../../components/checkbox/checkbox.component';
import { DownloadFormatOption } from '../on-demand-report-components/report-buttons/report-buttons.component';
import { ModalStates } from '../../../models/modal';
import { downloadFile } from '../../../tools/reports-tools';
import { ModalService } from '../../../services/modal.service';
import { AclService } from '../../../services/acl.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Pagination } from '../../../models/pagination';
import { PagedPayload } from '../../../models/paged-payload';

@Component({
  selector: 'lib-paystub-history',
  templateUrl: './paystub-history.component.html',
  styleUrls: ['./paystub-history.component.css'],
  standalone: false
})
export class PaystubHistoryComponent implements OnInit {
  @Input()
  public clientCode: string;
  @Input()
  public activatedRoute: ActivatedRoute;
  public years: ReportPaydate[] = [];
  public chosenYear: ReportPaydate | null = null;
  public paydates: BehaviorSubject<PaydateInfo[]>;
  public chosenPayDate: PaydateInfo | null;
  public checkSizes = CheckboxSizes;
  public fetchedPaylines: PayStubDataSelectable[] | null = null;
  public downloadFormats = [DownloadFormatOption.PDF()];
  public selectedLines: number[] = [];
  public selectAll: boolean = false;
  public canAccessPeople = false;
  public pagination: Pagination;

  public hideJumbo: boolean = false;
  constructor(
    private reportService: ReportsService,
    private companyService: CompaniesService,
    private modalService: ModalService,
    private aclService: AclService,
    private router: Router,
    @Inject('env') private env: any
  ) {}

  ngOnInit(): void {
    this.paydates = new BehaviorSubject<PaydateInfo[]>([]);
    this.reportService.getPayrollHistory(this.clientCode).subscribe(results => {
      if (results.length > 0) {
        this.years = results.sort((a, b) => {
          if (a.Year < b.Year) return 1;
          if (a.Year > b.Year) return -1;
          return 0;
        });
      }
    });
    this.aclService
      .CurrentUserAccessCheck('Client Portal Admin', 'EditPeople', this.clientCode)
      .subscribe(result => {
        if (!result) {
          this.aclService.CurrentUserHasMinimumRole('CSR1').subscribe(isCsr => {
            this.canAccessPeople = isCsr;
          });
        } else {
          this.canAccessPeople = result;
        }
      });
  }
  public checkChanged(val: any, stub: PayStubDataSelectable) {
    stub.Selected = val;
    if (val) {
      this.selectedLines.push(stub.PaylineId);
    } else {
      this.selectedLines = this.selectedLines.filter(x => x != stub.PaylineId);
    }
  }
  public yearChanged(newYear: ReportPaydate) {
    this.chosenYear = newYear;
    if (newYear != null) this.paydates.next(newYear.Paydates.sort(this._SortPaydates));
    else this.paydates.next([]);
    this.chosenPayDate = null;
  }

  private _SortPaydates(a: PaydateInfo, b: PaydateInfo) {
    if (a.Paydate < b.Paydate) return 1;
    if (a.Paydate > b.Paydate) return -1;
    if (a.SequenceNumber < b.SequenceNumber) return -1;
    if (a.SequenceNumber > b.SequenceNumber) return 1;
    return 0;
  }

  public paydateChanged(chosen: PaydateInfo) {
    this.chosenPayDate = chosen;
    this.hideJumbo = true;
    this.selectedLines = [];
    this.companyService
      .getPaylineData(
        this.clientCode,
        new Date(Date.parse(chosen.Paydate)).getFullYear(),
        chosen.SequenceNumber
      )
      .subscribe(x => this._ParsePaystubs(x));
  }
  public pageChanged({ Page }: Pagination) {
    this.companyService
      .getPaylineData(
        this.clientCode,
        new Date(Date.parse(this.chosenPayDate.Paydate)).getFullYear(),
        this.chosenPayDate.SequenceNumber,
        this.pagination.PerPage,
        Page
      )
      .subscribe(x => this._ParsePaystubs(x));
  }
  public selectAllChanged(val: boolean) {
    this.fetchedPaylines.forEach(stub => {
      this.checkChanged(val, stub);
    });
  }

  public reportGenerate() {
    const args = {
      PaylineIds: this.selectedLines,
      PayDate: this.chosenPayDate.Paydate,
      SeqNo: this.chosenPayDate.SequenceNumber
    };
    const year = new Date(Date.parse(this.chosenPayDate.Paydate));

    this.reportService
      .dynamicReport(args, 'application/pdf', this.clientCode, 'PayStubHistory')
      .subscribe((result: any) => {
        if (result == null) {
          this.modalService.showAlertModal(
            'The report parameters that you entered did not generate any results. Please try again with different parameters.',
            ModalStates.Error
          );
        } else {
          downloadFile(
            result,
            `Client Pay Stubs_${this.clientCode}_${year.getFullYear()}_PD${
              year.getMonth() + 1
            }${year.getDate()}${year.getFullYear()}.pdf`
          );
        }
      });
  }
  public personLinkClicked(stub: PayStubData) {
    if (this.canAccessPeople) {
      let url = this.router.serializeUrl(
        this.router.createUrlTree([`../person/${stub.Id}`], { relativeTo: this.activatedRoute })
      );
      url = `${this.env.siteUrlRoot}${url}`;
      console.log(url);
      window.open(url, '_blank');
    }
  }
  private _ParsePaystubs(data: PagedPayload<PayStubData>) {
    this.pagination = data.Pagination;
    this.fetchedPaylines = data.Items.map(y => {
      return {
        Id: y.Id,
        Name: y.Name,
        NetPay: y.NetPay,
        PersonId: y.PersonId,
        PaylineId: y.PaylineId,
        Selected: false
      };
    }).sort((a, b) => {
      if (a.Name < b.Name) return -1;
      if (b.Name < a.Name) return 1;
      if (a.PaylineId > b.PaylineId) return -1;
      if (a.PaylineId < b.PaylineId) return 1;
      return 0;
    });
  }
}

interface PayStubDataSelectable extends PayStubData {
  Selected: boolean;
}
